// Contract constants — sync với BE enum CONTRACT_STATUS (9 state).
// BE: backend/src/modules/contract/constants

export const CONTRACT_STATUS = {
  // Luồng mới: HĐ tạo ra vào thẳng "Chờ cọc". pendingSignature/signed giữ cho HĐ legacy.
  PENDING_DEPOSIT: 'pendingDeposit',
  PENDING_SIGNATURE: 'pendingSignature',
  SIGNED: 'signed',
  DEPOSITED: 'deposited',
  ALLOCATED: 'allocated',
  PAID: 'paid',
  INVOICED: 'invoiced',
  DELIVERED: 'delivered',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
};

export const CONTRACT_STATUS_LABEL = {
  [CONTRACT_STATUS.PENDING_DEPOSIT]: 'Chờ cọc',
  [CONTRACT_STATUS.PENDING_SIGNATURE]: 'Chờ ký',
  [CONTRACT_STATUS.SIGNED]: 'Đã ký',
  [CONTRACT_STATUS.DEPOSITED]: 'Đã đặt cọc',
  [CONTRACT_STATUS.ALLOCATED]: 'Đã phân bổ xe',
  [CONTRACT_STATUS.PAID]: 'Đã thanh toán',
  [CONTRACT_STATUS.INVOICED]: 'Đã tạo hóa đơn',
  [CONTRACT_STATUS.DELIVERED]: 'Đã giao xe',
  [CONTRACT_STATUS.COMPLETED]: 'Hoàn tất',
  [CONTRACT_STATUS.CANCELLED]: 'Đã hủy',
};

// Vuetify color names
export const CONTRACT_STATUS_COLOR = {
  [CONTRACT_STATUS.PENDING_DEPOSIT]: 'orange',
  [CONTRACT_STATUS.PENDING_SIGNATURE]: 'orange',
  [CONTRACT_STATUS.SIGNED]: 'blue',
  [CONTRACT_STATUS.DEPOSITED]: 'cyan',
  [CONTRACT_STATUS.ALLOCATED]: 'teal',
  [CONTRACT_STATUS.PAID]: 'success',
  [CONTRACT_STATUS.INVOICED]: 'indigo',
  [CONTRACT_STATUS.DELIVERED]: 'primary',
  [CONTRACT_STATUS.COMPLETED]: 'green darken-2',
  [CONTRACT_STATUS.CANCELLED]: 'grey',
};

// State machine — phải khớp BE backend/src/modules/contract/constants/state-machine.ts
// Luồng duyệt rút gọn: pendingSignature → signed → deposited → allocated → invoiced (+ cancelled).
//   signed    = DUYỆT HỢP ĐỒNG · deposited = ĐÃ THU CỌC · allocated = DUYỆT GIỮ HÀNG / PHÂN BỔ XE.
//   allocated = đủ điều kiện tạo hóa đơn (Bill) → không tiến state thủ công nữa.
//   invoiced  = "Đã tạo hóa đơn" — BE tự set khi tạo hóa đơn bán lẻ từ HĐ; tự về `allocated`
//               khi xóa hóa đơn đó. KHÔNG phải user action → map transition rỗng (terminal).
// paid/delivered/completed: chỉ còn trên HĐ legacy (paid vẫn cho cancel).
// FE tự duplicate map; nếu BE đổi → cập nhật tay.
export const CONTRACT_STATE_TRANSITION_MAP = {
  [CONTRACT_STATUS.PENDING_DEPOSIT]: [
    CONTRACT_STATUS.DEPOSITED,
    CONTRACT_STATUS.CANCELLED,
  ],
  [CONTRACT_STATUS.PENDING_SIGNATURE]: [
    CONTRACT_STATUS.SIGNED,
    CONTRACT_STATUS.CANCELLED,
  ],
  [CONTRACT_STATUS.SIGNED]: [
    CONTRACT_STATUS.DEPOSITED,
    CONTRACT_STATUS.CANCELLED,
  ],
  [CONTRACT_STATUS.DEPOSITED]: [
    CONTRACT_STATUS.ALLOCATED,
    CONTRACT_STATUS.CANCELLED,
  ],
  [CONTRACT_STATUS.ALLOCATED]: [CONTRACT_STATUS.CANCELLED],
  // Đã tạo hóa đơn → Giao xe → Hoàn tất. `invoiced → allocated` là tự động (xóa bill), không phải transition thủ công.
  // `invoiced → delivered` (Giao xe) bị BE chặn nếu bill VAT chưa "Đã xuất".
  [CONTRACT_STATUS.INVOICED]: [CONTRACT_STATUS.DELIVERED],
  // Legacy — vẫn cho cancel HĐ cũ đang ở state này.
  [CONTRACT_STATUS.PAID]: [CONTRACT_STATUS.CANCELLED],
  [CONTRACT_STATUS.DELIVERED]: [],
  [CONTRACT_STATUS.COMPLETED]: [],
  [CONTRACT_STATUS.CANCELLED]: [],
};

// Khóa sửa giá item khi HĐ đã giữ hàng (allocated) trở đi — sau đó đủ điều kiện tạo hóa đơn.
export const CONTRACT_PRICE_LOCKED_STATUSES = [
  CONTRACT_STATUS.ALLOCATED,
  CONTRACT_STATUS.PAID,
  CONTRACT_STATUS.INVOICED,
  CONTRACT_STATUS.DELIVERED,
  CONTRACT_STATUS.COMPLETED,
  CONTRACT_STATUS.CANCELLED,
];

// BootstrapVue badge variant — dùng cho list/table (khác Vuetify color ở trên).
export const CONTRACT_STATUS_VARIANT = {
  [CONTRACT_STATUS.PENDING_DEPOSIT]: 'warning',
  [CONTRACT_STATUS.PENDING_SIGNATURE]: 'warning',
  [CONTRACT_STATUS.SIGNED]: 'info',
  [CONTRACT_STATUS.DEPOSITED]: 'info',
  [CONTRACT_STATUS.ALLOCATED]: 'info',
  [CONTRACT_STATUS.PAID]: 'success',
  [CONTRACT_STATUS.INVOICED]: 'primary',
  [CONTRACT_STATUS.DELIVERED]: 'primary',
  [CONTRACT_STATUS.COMPLETED]: 'success',
  [CONTRACT_STATUS.CANCELLED]: 'danger',
};

// Options cho dropdown (BDropdownCustom dùng { value, text }) — đủ 9 state, dùng khi cần
// hiển thị/gán mọi trạng thái (kể cả legacy paid/invoiced/delivered/completed).
export const CONTRACT_STATUS_OPTIONS = Object.values(CONTRACT_STATUS).map(
  (v) => ({ value: v, text: CONTRACT_STATUS_LABEL[v] || v }),
);

// Options cho dropdown FILTER ở danh sách HĐ — chỉ các trạng thái thực tế đang dùng:
// luồng rút gọn pendingSignature → signed → deposited → allocated → invoiced + cancelled.
// (Legacy paid/delivered/completed cố tình bỏ khỏi filter — nếu cần lọc HĐ cũ
//  ở các state đó thì thêm lại vào mảng dưới.)
export const CONTRACT_STATUS_FILTER_VALUES = [
  // Luồng mới: bỏ "Chờ ký"/"Đã ký" khỏi filter, thay bằng "Chờ cọc".
  // "Đã tạo hóa đơn" = đã bán chưa giao; "Đã giao xe" = bước cuối (đã bỏ "Hoàn tất").
  CONTRACT_STATUS.PENDING_DEPOSIT,
  CONTRACT_STATUS.DEPOSITED,
  CONTRACT_STATUS.ALLOCATED,
  CONTRACT_STATUS.INVOICED,
  CONTRACT_STATUS.DELIVERED,
  CONTRACT_STATUS.CANCELLED,
];

export const CONTRACT_STATUS_FILTER_OPTIONS = CONTRACT_STATUS_FILTER_VALUES.map(
  (v) => ({ value: v, text: CONTRACT_STATUS_LABEL[v] || v }),
);
